import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/http';
import { useBot, useBotLifecycle, useChannels } from '../../api/hooks';
import { DESIRED_LABEL, EFFECTIVE_LABEL, CHANNEL_LABEL } from '../../domain/labels';
import { Avatar, Breadcrumbs, Button, DataTable, DescriptionList, Dialog, ErrorNote, IdChip, Loading, PageHeader, RelativeTime, ScopeList, Section, StatePill, Tabs } from '../../ui';
import { useState } from 'react';

export function BotLayout(): JSX.Element {
  const { botId = '' } = useParams();
  const bot = useBot(botId);
  if (bot.isPending) return <Loading />;
  if (bot.isError || !bot.data) return <ErrorNote>Bot introuvable</ErrorNote>;
  const data = bot.data;
  return (
    <>
      <Breadcrumbs items={[{ to: '/', label: 'Bots' }, { label: data.name }]} />
      <PageHeader
        eyebrow="Bot"
        title={
          <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
            <Avatar src={data.identity?.avatarUrl} name={data.name} />
            {data.name}
          </span>
        }
        actions={<BotActions botId={botId} desired={data.desiredState} />}
      >
        <p className="muted">@{data.identity?.login ?? data.id}</p>
      </PageHeader>
      <Tabs
        items={[
          { to: `/bots/${botId}`, label: 'Vue d’ensemble', end: true },
          { to: `/bots/${botId}/channels`, label: 'Chaînes' },
          { to: `/bots/${botId}/enrollments`, label: 'Invitations' },
          { to: `/bots/${botId}/authorization`, label: 'Autorisation' },
          { to: `/bots/${botId}/activity`, label: 'Activité' },
        ]}
      />
      <Outlet />
    </>
  );
}

function BotActions({ botId, desired }: { botId: string; desired: string }): JSX.Element {
  const nav = useNavigate();
  const { start, stop, remove } = useBotLifecycle(botId);
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      {desired === 'running' ? (
        <Button onClick={() => stop.mutate()} loading={stop.isPending}>Arrêter</Button>
      ) : (
        <Button variant="primary" data-testid={`start-${botId}`} onClick={() => start.mutate()} loading={start.isPending}>
          Démarrer
        </Button>
      )}
      <Button onClick={() => { window.location.href = api.botReauthOauthUrl(botId); }}>Réauthentifier</Button>
      <Button variant="danger" onClick={() => setConfirm(true)}>Supprimer</Button>
      <Dialog open={confirm} title="Supprimer ce bot ?" danger confirmLabel="Supprimer" onClose={() => setConfirm(false)} onConfirm={() => { remove.mutate(); nav('/'); }}>
        Cette action retire le bot et arrête ses abonnements EventSub.
      </Dialog>
    </>
  );
}

export function BotOverviewPage(): JSX.Element {
  const { botId = '' } = useParams();
  const bot = useBot(botId);
  if (!bot.data) return <Loading />;
  const data = bot.data;
  return (
    <Section title="Santé">
      <DescriptionList
        items={[
          { label: 'Intent', value: <StatePill state={data.desiredState}>{DESIRED_LABEL[data.desiredState]}</StatePill> },
          { label: 'Effectif', value: <StatePill state={data.health.effectiveState}>{EFFECTIVE_LABEL[data.health.effectiveState]}</StatePill> },
          { label: 'Dernière réconciliation', value: <RelativeTime value={data.health.lastReconciledAt} /> },
          { label: 'Identifiant', value: <IdChip value={data.id} /> },
          { label: 'Chaînes', value: data.counts.channels },
          { label: 'EventSub actifs', value: data.counts.subscriptionsEnabled },
          { label: 'Scopes manquants', value: data.counts.subscriptionsGrantPending },
        ]}
      />
    </Section>
  );
}

export function BotChannelsPage(): JSX.Element {
  const { botId = '' } = useParams();
  const channels = useChannels(botId);
  if (channels.isPending) return <Loading />;
  return (
    <DataTable
      rows={channels.data ?? []}
      empty={<span>Aucune chaîne. <Link to={`/bots/${botId}/enrollments`}>Créer une invitation</Link>.</span>}
      columns={[
        {
          key: 'ch',
          header: 'Chaîne',
          cell: (c) => (
            <span className="tbm-toolbar">
              <Link to={`/bots/${botId}/channels/${c.broadcasterUserId}`}>{c.channel || c.login}</Link>
              <Link to={`/bots/${botId}/channels/${c.broadcasterUserId}/helix`}>Actions</Link>
            </span>
          ),
        },
        { key: 'st', header: 'Statut', cell: (c) => <StatePill state={c.status}>{CHANNEL_LABEL[c.status]}</StatePill> },
        { key: 'sc', header: 'Scopes', cell: (c) => <ScopeList granted={c.grantedScopes} /> },
      ]}
    />
  );
}

export function BotAuthPage(): JSX.Element {
  const { botId = '' } = useParams();
  const bot = useBot(botId);
  if (!bot.data) return <Loading />;
  const auth = bot.data.authorization;
  const identity = bot.data.identity;
  return (
    <Section title="Coffre d’autorisation">
      <DescriptionList
        items={[
          { label: 'Compte Twitch', value: identity ? `@${identity.login}` : '—' },
          { label: 'User id', value: identity ? <IdChip value={identity.twitchUserId} /> : '—' },
          { label: 'Statut', value: auth ? <StatePill state={auth.status}>{auth.status}</StatePill> : 'absent' },
          { label: 'Expiration', value: <RelativeTime value={auth?.expiresAt} /> },
          { label: 'Refresh', value: auth?.hasRefreshToken ? 'oui' : 'non' },
          { label: 'Scopes', value: <ScopeList granted={auth?.grantedScopes ?? []} /> },
        ]}
      />
    </Section>
  );
}
